import React, { createContext, memo, useContext, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { jsx as _jsx } from "react/jsx-runtime";
var PageTitleAnnouncerContext = /*#__PURE__*/createContext(undefined);

/**
 * This approach for adding a custom context hook was taken from the following article:
 * https://reacttraining.com/blog/react-context-with-typescript/
 */

function usePageTitleAnnouncerContext() {
  var context = useContext(PageTitleAnnouncerContext);
  if (context === undefined) {
    throw Error('usePageTitleAnnouncer must be used inside of a PageTitleAnnouncer');
  }
  return context;
}
function Provider(_ref) {
  var pathname = _ref.pathname,
    children = _ref.children;
  var hasNavigationOccuredRef = useRef(false);
  useEffect(function () {
    // Track whether any navigation has occured, as we
    // don't want to move user focus on initial page load
    return function () {
      hasNavigationOccuredRef.current = true;
    };
  }, [pathname]);
  return /*#__PURE__*/_jsx(PageTitleAnnouncerContext.Provider, {
    value: hasNavigationOccuredRef,
    children: children
  });
}
function memoCondition(prevProps, nextProps) {
  return prevProps.pathname === nextProps.pathname;
}

// Memoise the component to make sure the component is only ever
// re-rendered when the `pathname` prop changes
var PageTitleAnnouncer = /*#__PURE__*/memo(Provider, memoCondition);
function usePageTitleAnnouncer(pageTitle) {
  var _state$focusPageTitle;
  var focusablePageTitleRef = useRef(null);
  var hasNavigationOccuredRef = usePageTitleAnnouncerContext();
  var _useLocation = useLocation(),
    state = _useLocation.state;
  var focusPageTitle = (_state$focusPageTitle = state === null || state === void 0 ? void 0 : state.focusPageTitle) !== null && _state$focusPageTitle !== void 0 ? _state$focusPageTitle : true;
  useEffect(function () {
    if (pageTitle && focusPageTitle && hasNavigationOccuredRef.current) {
      var _focusablePageTitleRe;
      (_focusablePageTitleRe = focusablePageTitleRef.current) === null || _focusablePageTitleRe === void 0 || _focusablePageTitleRe.focus();
    }
  }, [pageTitle, focusPageTitle, hasNavigationOccuredRef]);
  return focusablePageTitleRef;
}
export { PageTitleAnnouncer, usePageTitleAnnouncer };